import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as CompleteIcon } from '@repo/common/assets/icons/20px/icon_20px_check-circle_outline.svg';
import { ReactComponent as LockIcon } from '@repo/common/assets/icons/16px/icon_16px_lock_outline.svg';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check-circle_outline.svg';
import { cn } from '../model/derive';
import { formatHm } from '../model/time';
import { kstClock, kstDue } from '../model/completeProject';
import TdButton from '../Button';
import { Tooltip } from './ui';

/** 워크보드의 Complete project 진입점 (사이드바 메뉴 · 상단 배너 · 사이드바 아래 카드). 코치마크 5/5 는 사이드바 메뉴를 가리킨다 */

const LOCK_TIP = 'Opens when the leader submits the final deliverable, or when the project ends.';

const MENU_BADGE = {
  open: [
    'Open',
    'bg-surface-green10 text-green-deep04 shadow-[inset_0_0_0_1px_rgba(0,158,79,0.4)]',
  ],
  late: [
    'Late',
    'bg-functional-warning01 text-functional-warning04 shadow-[inset_0_0_0_1px_#ffdf56]',
  ],
  closed: ['Closed', 'bg-grayscale-gray01 text-text-tertiary shadow-[inset_0_0_0_1px_#d5d2cd]'],
};

export const CompleteProjectMenuItem = ({ cp, to }) => {
  const [tip, setTip] = useState(false);
  const locked = cp.kind === 'locked';
  const off = locked || cp.kind === 'closed';
  const badge =
    cp.kind === 'closed'
      ? MENU_BADGE.closed
      : cp.kind === 'open'
        ? MENU_BADGE[cp.step === 1 ? 'late' : 'open']
        : null;
  const Icon = off ? LockIcon : CompleteIcon;
  const body = (
    <>
      <span className='flex h-5 w-5 shrink-0 items-center justify-center'>
        <Icon className={cn(off ? 'h-4 w-4 text-text-disable' : 'h-5 w-5 text-icon-secondary')} />
      </span>
      <span
        className={cn(
          'min-w-0 flex-1 [text-wrap:pretty]',
          off ? 'text-text-disable' : 'text-text-tertiary',
        )}
      >
        Complete project
      </span>
      {badge && (
        <span
          className={cn(
            'inline-flex h-5 shrink-0 items-center rounded-md px-[7px] text-[11px] font-medium',
            badge[1],
          )}
        >
          {badge[0]}
        </span>
      )}
      {cp.kind === 'done' && (
        <CheckIcon
          aria-label='Completed'
          className='mt-0.5 h-4 w-4 shrink-0 text-functional-success03'
        />
      )}
    </>
  );
  const cls =
    'relative flex min-h-9 items-start gap-2.5 rounded-lg py-2 pl-3 pr-2.5 text-[14px] leading-[140%]';

  if (locked) {
    return (
      <button
        type='button'
        aria-disabled='true'
        aria-describedby={tip ? 'cp-lock-tip' : undefined}
        onClick={() => setTip((v) => !v)}
        onMouseEnter={() => setTip(true)}
        onMouseLeave={() => setTip(false)}
        onFocus={() => setTip(true)}
        onBlur={() => setTip(false)}
        data-coach='complete-project'
        className={cn(cls, 'cursor-default text-left')}
      >
        {body}
        <Tooltip show={tip}>
          <span id='cp-lock-tip'>{LOCK_TIP}</span>
        </Tooltip>
      </button>
    );
  }
  return (
    <Link to={to} data-coach='complete-project' className={cn(cls, 'hover:bg-green-deep03/[0.06]')}>
      {body}
    </Link>
  );
};

export const CompleteProjectBanner = ({ cp, to }) => {
  if (cp.kind !== 'open') return null;
  const late = cp.step === 1;
  let title = `Peer review is open · ${formatHm(cp.leftMin)} left`;
  let desc = '';
  if (cp.paused) title += ' · paused while you’re offline';
  if (cp.reset) desc = 'The deadline moved because the final deliverable was resubmitted.';
  if (late) {
    title = `Peer review is late · closes ${kstDue(cp.closesAt)}`;
    desc =
      'Still counts as completed, but your report shows “Peer review submitted late” and Kit reference sentences can’t be used.';
  }
  return (
    <div
      role='status'
      className={cn(
        'mb-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5 rounded-xl border px-4 py-3',
        late
          ? 'border-functional-warning02 bg-functional-warning01'
          : 'border-green-deep03/[0.12] bg-surface-green10',
      )}
    >
      <div className='flex min-w-0 flex-[1_1_260px] gap-2.5'>
        <span
          aria-hidden
          className={cn(
            'mt-2 h-1.5 w-1.5 shrink-0 rounded-full',
            late ? 'bg-functional-warning03' : 'bg-green-deep02',
          )}
        />
        <div className='flex min-w-0 flex-col gap-0.5'>
          <span
            className={cn(
              'text-[14px] font-medium leading-[150%] tabular-nums',
              late ? 'text-functional-warning04' : 'text-text-primary',
            )}
          >
            {title}
          </span>
          {desc && (
            <span className='text-[13px] leading-[150%] text-text-secondary [text-wrap:pretty]'>
              {desc}
            </span>
          )}
        </div>
      </div>
      <TdButton
        as={Link}
        to={to}
        variant='soft'
        className='w-full shrink-0 bg-grayscale-gray00 px-[18px] shadow-[inset_0_0_0_1px_rgba(0,158,79,0.2)] hover:bg-[#f0f9f4] md:w-auto'
      >
        Start
      </TdButton>
    </div>
  );
};

/** 사이드바 아래 Complete project 카드 (SB_CompleteBanner) — 동료 평가가 열렸을 때만. to 가 있으면 버튼이 링크가 된다 */
export const CoachCompleteCard = ({ cp, to }) => {
  const late = cp.kind === 'open' && cp.step === 1;
  let countdown = 'Peer review · 3 working hours';
  let deadline = 'Opens when the leader submits the final deliverable';
  if (cp.kind === 'open' && !late) {
    countdown = `Peer review due in ${formatHm(cp.leftMin)}`;
    deadline = `Due ${kstDue(cp.dueAt)} · 3 hours after final handoff`;
  } else if (late) {
    countdown = `Late submission · final deadline in ${formatHm(cp.lateLeftMin)}`;
    deadline = `Final deadline ${kstDue(cp.closesAt)} · after that: not completed`;
  } else if (cp.kind === 'done') {
    countdown = `Submitted ${kstClock(cp.submittedAt)}`;
    deadline = 'You’ve completed the project';
  }
  return (
    <div
      className={cn(
        'mt-2.5 flex flex-col gap-2.5 rounded-xl border bg-grayscale-gray00 p-3.5',
        late ? 'border-functional-warning02' : 'border-green-deep03/20',
      )}
    >
      <div className='flex items-center justify-between gap-2'>
        <span className='text-[14px] font-medium leading-[140%] text-text-primary'>
          Complete project
        </span>
        {late && (
          <span className='inline-flex h-5 items-center gap-[5px] rounded-md bg-functional-warning01 px-[7px] text-[11px] font-medium text-functional-warning04 shadow-[inset_0_0_0_1px_#ffdf56]'>
            <span className='h-[5px] w-[5px] rounded-full bg-functional-warning03' />
            Late
          </span>
        )}
      </div>
      <div className='flex flex-col gap-0.5'>
        <span
          className={cn(
            'text-[15px] font-medium leading-[140%]',
            late ? 'text-functional-warning04' : 'text-text-primary',
          )}
        >
          {countdown}
        </span>
        <span className='text-[12px] leading-[140%] text-text-secondary'>{deadline}</span>
      </div>
      {to ? (
        <TdButton as={Link} to={to} variant='soft' className='w-full'>
          Complete project
        </TdButton>
      ) : (
        <span className='flex h-10 items-center justify-center rounded-full bg-surface-green10 text-[14px] font-medium text-text-primary'>
          Complete project
        </span>
      )}
    </div>
  );
};
